import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { useMutation } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import type {
  AgentPlaygroundFormValues,
  PlaygroundSessionState,
  RuntimeByCategory,
} from "src/api/agents/execution";
import {
  getSessionSystemPrompt,
  useAgentExecutionClient,
  useAgentSession,
  useCancelSession,
} from "src/api/agents/execution";
import type {
  AgentInvokeResponse,
  AgentPlaygroundInvokeRequest,
  Data,
  SessionResponse,
} from "src/clients/agent-management";
import { Environment, ExecutionMode } from "src/clients/agent-management";

const PREWARM_COOLDOWN_MS = 20_000; // dedup window in ms

/**
 * Module-level storage for the session ID reserved for the next run.
 * - Fresh ID minted per agent/version on first access after page load
 * - Automatically reset on page refresh
 * - Isolated per browser tab
 *
 * The frontend owns session IDs for playground runs: each run's session ID is
 * minted ahead of time and its AgentCore container pre-warmed via ping, so the
 * invoke lands on the warm container keyed by that same session ID.
 */
const reservedSessionIdMap = new Map<string, string>();

/**
 * Module-level ping promise tracking, keyed by session ID.
 * An invoke waits for the in-flight ping of its own session ID to complete
 * to avoid concurrent requests to the same container session.
 */
const pingPromiseMap = new Map<string, Promise<void>>();

/**
 * Subscription infrastructure for ping state.
 * Allows React components to subscribe to ping state changes via useSyncExternalStore.
 */
const pingListeners = new Set<() => void>();

const subscribeToPingState = (callback: () => void): (() => void) => {
  pingListeners.add(callback);
  return () => pingListeners.delete(callback);
};

const notifyPingListeners = (): void => {
  pingListeners.forEach((cb) => cb());
};

const getOrCreateReservedSessionId = (key: string): string => {
  let id = reservedSessionIdMap.get(key);
  if (!id) {
    id = uuidv4();
    reservedSessionIdMap.set(key, id);
  }
  return id;
};

/**
 * Returns the currently reserved session ID and reserves a fresh one for the
 * following run.
 */
const rotateReservedSessionId = (
  key: string,
): { current: string; next: string } => {
  const current = getOrCreateReservedSessionId(key);
  const next = uuidv4();
  reservedSessionIdMap.set(key, next);
  return { current, next };
};

type PlaygroundInvokeParams = {
  inputSchema: Record<string, unknown> | null;
  submittedInput: AgentPlaygroundFormValues;
};

/**
 * Internal session state machine for tracking the client-side session lifecycle.
 * This is the root state managed by the client. {@link PlaygroundSessionState} is
 * derived from this combined with server-side session data.
 * - idle: No session active
 * - active: Session in progress (running, paused, or completed)
 * - stopped: UI stopped tracking, wants to be able to start a new run
 */
type InternalSessionState =
  | { type: "idle" }
  | {
      type: "active";
      sessionId: string;
      inputSchema: Record<string, unknown> | null;
      submittedInput: AgentPlaygroundFormValues;
    }
  | {
      type: "stopped";
      sessionId: string;
      inputSchema: Record<string, unknown> | null;
      submittedInput: AgentPlaygroundFormValues;
    };

/**
 * Invoke mutation interface exposed by the context.
 * Simplified from full UseMutationResult to only what consumers need.
 */
type PlaygroundInvokeMutation = {
  mutate: (
    params: PlaygroundInvokeParams,
    options?: { onError?: () => void },
  ) => void;
  isPending: boolean;
};

type PlaygroundContextValue = {
  sessionState: PlaygroundSessionState;
  /** Raw session data from the API, useful for debugging/copy features */
  rawSessionData: SessionResponse | undefined;
  ping: () => void;
  /**
   * Stops the current session UI-side only.
   * The backend session continues, but we stop polling and allow input editing.
   * Messages remain visible.
   */
  stopSession: () => void;
  invoke: PlaygroundInvokeMutation;
  /**
   * Register a start test handler from AgentPlayground.
   * This allows the header button to trigger form validation and submission.
   */
  registerStartTest: (handler: () => void) => void;
  /**
   * Trigger the registered start test handler.
   * Called by the header button when playground is open but not running.
   */
  startTest: () => void;
};

const PlaygroundContext = createContext<PlaygroundContextValue | null>(null);

type PlaygroundProviderProps = {
  agentId: string;
  versionId: string;
  children: React.ReactNode;
};

/**
 * Provides playground session state to children.
 *
 * - Owns session IDs for playground runs (fresh IDs per page load, per tab)
 * - The session ID reserved for the next run is minted ahead of time and its
 *   container pre-warmed: as soon as a run starts, the following run's session
 *   ID is minted and pinged
 * - Uses a cooldown window to deduplicate rapid ping triggers
 * - An invoke waits for its own session's in-flight ping (prevents 502 errors
 *   from concurrent requests to the same agent runner container session)
 * - Exposes frontend session status for UI display (e.g. "Booting..." vs "Thinking...")
 */
export const PlaygroundProvider: React.FC<PlaygroundProviderProps> = ({
  agentId,
  versionId,
  children,
}) => {
  const { client } = useAgentExecutionClient();
  const lastPingRef = useRef<number>(0);
  const key = `${agentId}-${versionId}`;

  // Internal session state machine: idle | active | stopped
  const [internalState, setInternalState] = useState<InternalSessionState>({
    type: "idle",
  });

  const currentSessionId =
    internalState.type !== "idle" ? internalState.sessionId : null;

  // Track ping in-flight state for the active session, for UI ("Booting...").
  // We use useSyncExternalStore instead of useState to avoid stale setter issues:
  // if user navigates away while ping is in-flight, the .finally() callback would
  // call the unmounted component's setter, leaving new component's state stale.
  const isActiveSessionPingInFlight = useSyncExternalStore(
    subscribeToPingState,
    () => (currentSessionId ? pingPromiseMap.has(currentSessionId) : false),
    () => false,
  );

  const firePing = useCallback(
    (targetSessionId: string) => {
      if (!client) return;

      lastPingRef.current = Date.now();

      const pingPromise = client
        .pingAgentsManagementApiV0PingPost({
          pingRequest: {
            agent_id: agentId,
            session_id: targetSessionId,
          },
        })
        .catch(() => {}) // Swallow errors
        .finally(() => {
          if (pingPromiseMap.get(targetSessionId) !== pingPromise) {
            // Outdated/stale promise, don't clean up the newer one
            return;
          }
          pingPromiseMap.delete(targetSessionId);
          notifyPingListeners();
        }) as unknown as Promise<void>;

      pingPromiseMap.set(targetSessionId, pingPromise);
      notifyPingListeners();
    },
    [client, agentId],
  );

  // Query session data - always pass sessionId to preserve cached data,
  // but only poll when session is active (not when stopped)
  const shouldPoll = internalState.type === "active";
  const sessionQuery = useAgentSession(currentSessionId, {
    polling: shouldPoll,
  });

  // Compute the unified PlaygroundSessionState from internal state and query data
  const sessionState: PlaygroundSessionState = (() => {
    const messages = sessionQuery.data?.messages ?? [];
    const serverStatus = sessionQuery.data?.status ?? null;

    if (internalState.type === "idle") {
      // No session - idle state
      return { status: "idle" as const };
    }

    const baseState = {
      sessionId: internalState.sessionId,
      sessionMessages: messages,
      inputSchema: internalState.inputSchema,
      systemPrompt: getSessionSystemPrompt(sessionQuery.data),
      submittedInput: internalState.submittedInput,
      liveStatusDetail: sessionQuery.data?.live_status_detail ?? null,
      runtimeByCategory:
        (sessionQuery.data?.metrics?.execution?.runtime_by_category as
          | RuntimeByCategory
          | undefined
          | null) ?? {},
    };

    if (internalState.type === "stopped") {
      // UI stopped tracking - cancelled substatus
      return {
        ...baseState,
        status: "stopped",
        substatus: "cancelled",
      };
    }

    // internalState.type === "active"
    // Map server status to our status/substatus model
    if (serverStatus === "success") {
      const result = sessionQuery.data?.result;
      if (result) {
        return {
          ...baseState,
          status: "stopped",
          substatus: "success",
          result,
        };
      }
    }

    if (serverStatus === "failed") {
      return {
        ...baseState,
        status: "stopped",
        substatus: "failed",
        terminationReason: sessionQuery.data?.termination_reason ?? "error",
        terminationMessage: sessionQuery.data?.termination_message ?? null,
      };
    }

    if (serverStatus === "cancelled") {
      return {
        ...baseState,
        status: "stopped",
        substatus: "cancelled",
      };
    }

    if (serverStatus === "paused") {
      return {
        ...baseState,
        status: "active",
        substatus: "paused",
      };
    }

    // For pending, running, or no server status yet
    // Check if we're still booting (this session's ping in flight and no server response)
    if (isActiveSessionPingInFlight && !serverStatus) {
      return {
        ...baseState,
        status: "active",
        substatus: "booting",
      };
    }

    // Default to running (covers 'pending', 'running', or optimistic state)
    return {
      ...baseState,
      status: "active",
      substatus: "running",
    };
  })();

  const ping = useCallback(() => {
    const reservedSessionId = getOrCreateReservedSessionId(key);

    // Skip if this session's ping is already in-flight
    if (pingPromiseMap.has(reservedSessionId)) {
      return;
    }

    // Dedupe: skip if pinged within cooldown window
    if (Date.now() - lastPingRef.current < PREWARM_COOLDOWN_MS) {
      return;
    }

    firePing(reservedSessionId);
  }, [key, firePing]);

  const cancelSession = useCancelSession();

  /**
   * Stops the current session: triggers a real backend cancellation AND
   * locally marks the session as stopped so the UI allows input editing
   * while keeping messages visible. The next run's container needs no
   * preparation here: its session ID was already minted and pinged when the
   * current run started.
   *
   * The UI-side stop is synchronous; the backend cancel orchestration
   * (markCancelling + mutation + error rollback) lives in
   * `useCancelSession`.
   */
  const stopSession = useCallback(() => {
    if (internalState.type === "active") {
      const sessionIdToCancel = internalState.sessionId;

      // Backend cancel + optimistic "Cancelling..." flag in one call.
      cancelSession(sessionIdToCancel);

      setInternalState({
        type: "stopped",
        sessionId: internalState.sessionId,
        inputSchema: internalState.inputSchema,
        submittedInput: internalState.submittedInput,
      });
    }
  }, [internalState, cancelSession]);

  // Ref to store the registered start test handler from AgentPlayground
  const startTestHandlerRef = useRef<(() => void) | null>(null);

  const registerStartTest = useCallback((handler: () => void) => {
    startTestHandlerRef.current = handler;
  }, []);

  const startTest = useCallback(() => {
    startTestHandlerRef.current?.();
  }, []);

  /**
   * Waits for the in-flight ping of the given session to complete.
   * Used by invoke to ensure sequential requests to AgentCore during coldstarts.
   */
  const waitForSessionPing = useCallback(
    async (sessionId: string): Promise<void> => {
      await pingPromiseMap.get(sessionId);
    },
    [],
  );

  // Internal params type that includes the session ID
  type InternalInvokeParams = PlaygroundInvokeParams & {
    sessionId: string;
  };

  // Internal mutation that receives all params including the session ID
  const { mutate: internalMutate, isPending } = useMutation({
    mutationFn: async (
      params: InternalInvokeParams,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }

      // Wait for this session's pre-warm ping to complete first
      await waitForSessionPing(params.sessionId);

      const { structuredInput, instructions } = params.submittedInput;

      const requestData: AgentPlaygroundInvokeRequest = {
        agent_id: agentId,
        agent_version_id: versionId,
        invocation_message: instructions,
        session_id: params.sessionId,
        type: "invoke",
        source: "playground",
        // Playground runs are sandbox-only
        control: {
          execution_mode: ExecutionMode.ASYNC,
          environment: Environment.SANDBOX,
        },
        // Only add structured_input if it has at least 1 field
        ...(Object.keys(structuredInput).length > 0
          ? { structured_input: structuredInput }
          : {}),
      };

      const response = await client.invokeAgentAgentsManagementApiV0InvokePost({
        data: requestData as Data,
      });

      return response.data;
    },
    onError: () => {
      // Clear session on error so user can retry
      setInternalState({ type: "idle" });
    },
  });

  // Wrapper that assigns session IDs and updates state before calling the mutation
  const invoke: PlaygroundInvokeMutation = useMemo(
    () => ({
      isPending,
      mutate: (
        params: PlaygroundInvokeParams,
        options?: { onError?: () => void },
      ) => {
        // Consume the reserved (pre-warmed) session ID for this run and
        // reserve a fresh one for the next run
        const { current: sessionId, next: nextSessionId } =
          rotateReservedSessionId(key);

        // Set internal state immediately
        setInternalState({
          type: "active",
          sessionId,
          inputSchema: params.inputSchema,
          submittedInput: params.submittedInput,
        });

        // Call mutation with all params including the session ID
        internalMutate({ ...params, sessionId }, options);

        // Prepare the container for the next run as soon as this run starts
        // (bypasses cooldown)
        firePing(nextSessionId);
      },
    }),
    [internalMutate, isPending, key, firePing],
  );

  return (
    <PlaygroundContext
      value={{
        sessionState,
        rawSessionData: sessionQuery.data,
        ping,
        stopSession,
        invoke,
        registerStartTest,
        startTest,
      }}
    >
      {children}
    </PlaygroundContext>
  );
};

/**
 * Hook to access playground session state. Must be used within a PlaygroundProvider.
 */
export const usePlaygroundContext = (): PlaygroundContextValue => {
  const context = useContext(PlaygroundContext);
  if (!context) {
    throw new Error(
      "usePlaygroundContext must be used within PlaygroundProvider",
    );
  }
  return context;
};

/**
 * Reset module-level state for testing purposes.
 */
export const __resetPlaygroundState = (): void => {
  reservedSessionIdMap.clear();
  pingPromiseMap.clear();
  pingListeners.clear();
};
